import {Injectable} from '@angular/core';
import { FeatureCollection } from 'geojson';
import { take } from 'rxjs/operators';
import { MapService } from 'src/app/shared/components/map/map.service';
import { ProjectQuery } from '../../projects/state/project.query';
import { ProjectService } from '../../projects/state/project.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectDemographicAreasService {

  static LAYER_PREFIX = 'mitra-demographic';
  static SOURCE_ID = ProjectDemographicAreasService.LAYER_PREFIX + '-source';
  static DEMOGRAPHIC_LAYER_ID = ProjectDemographicAreasService.LAYER_PREFIX + '-area';
  static DEMOGRAPHIC_LAYER_LABEL_ID = ProjectDemographicAreasService.LAYER_PREFIX + '-label';

  areas: FeatureCollection;
  layers: string[] = [''];

  constructor(
    private projectService: ProjectService,
    private projectQuery: ProjectQuery,
    private mapService: MapService
  ) {
      // this.load();
      this.mapService.styleChanged$.subscribe(() => this.load());
      this.projectQuery.active$.subscribe(() => this.load());
  }

  public getAllLayerIds(): string[] {
    return this.layers.map(l => this.demLayerId(l));
  }

  public getAllLabelLayersIds(): string[] {
    return this.layers.map(l => this.demLayerLabelId(l));
  }

  public demLayerId(layer: string): string {
    return `${ProjectDemographicAreasService.DEMOGRAPHIC_LAYER_ID}-${layer}`;
  }

  public demLayerLabelId(layer: string): string {
    return `${ProjectDemographicAreasService.DEMOGRAPHIC_LAYER_LABEL_ID}-${layer}`;
  }

  public demLayerSourceId(layer: string): string {
    return `${ProjectDemographicAreasService.SOURCE_ID}-${layer}`;
  }

  public clearLayers(): void {
    this.mapService.map$.subscribe(mapbox => {

      this.layers.forEach(layerID => {
        if (mapbox.getLayer(this.demLayerId(layerID))) {
          mapbox.removeLayer(this.demLayerId(layerID));
        }
        if (mapbox.getLayer(this.demLayerLabelId(layerID))) {
          mapbox.removeLayer(this.demLayerLabelId(layerID));
        }
        if (mapbox.getSource(this.demLayerSourceId(layerID))) {
          mapbox.removeSource(this.demLayerSourceId(layerID));
        }
      });
    });
    this.layers = [];
  }

  public load() {
    this.clearLayers();
    const projectId = this.projectQuery.getActiveId();
    if (projectId) {
      this.projectService.getDemographicAreas(projectId)
        .pipe(take(1))
        .subscribe(result => {
          this.loadAreas(result);
          this.create();
        });
    }
  }

  private create(): void {
    this.mapService.map$.subscribe(mapbox => {
      this.createSource(mapbox);
      this.createLayer(mapbox);
    });
  }

  private createSource(mapbox: mapboxgl.Map) {
    this.layers.forEach(layer => {
      const layerData = {
        type: 'FeatureCollection',
        features: this.areas.features.filter(f => f.properties.class === layer)
      } as FeatureCollection;
      mapbox.addSource(this.demLayerSourceId(layer), {
        type: 'geojson',
        data: layerData
      });
    });
  }

  private createLayer(mapbox: mapboxgl.Map) {
    const firstLayer = this.getFirstMitraLayer(mapbox);
    this.layers.forEach(layer => {
      mapbox.addLayer(this.layerFill(layer), firstLayer.id);
      mapbox.addLayer(this.layerLabel(layer));
    });
  }

  private getFirstMitraLayer(mapbox: mapboxgl.Map) {
    return mapbox.getStyle().layers.find(l => l.id.startsWith('mitra-'));
  }

  private layerFill(layerID: string): mapboxgl.Layer {
    return {
      id: this.demLayerId(layerID),
      source: this.demLayerSourceId(layerID),
      type: 'fill',
      layout: {},
      paint: {
        'fill-color': this.fillColorFor(layerID),
        'fill-opacity': 0.35
      }
    } as mapboxgl.Layer;
  }

  private layerLabel(layerID: string): mapboxgl.Layer {
    return {
      id: this.demLayerLabelId(layerID),
      type: 'symbol',
      source: this.demLayerSourceId(layerID),
      layout: {
        'text-field': ['get', 'class'],
        'text-anchor': 'center',
        'text-size': 20,
        'text-radial-offset': 0.5
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-blur': 1,
        'text-halo-width': 1
      }
    } as mapboxgl.Layer;
  }

  private loadAreas(result: FeatureCollection) {
    this.areas = result;
    this.layers = this.areas.features.map(feature => feature.properties.class).
    filter((value, index, self) => self.indexOf(value) === index);
  }

  private fillColorFor(layerID: string): string {
    switch (layerID) {
      case 'A': return '#F9FF33';
      case 'B': return '#9633FF';
      case 'C': return '#71FF33';
      case 'D': return '#89263A';
    }
  }
}








